import React, { useState, useEffect } from 'react';
import { calendrierAPI } from '../services/api';
import AppSidebar, { getModuleTopbarColor } from './AppSidebar';
import './EdtBuilder.css';

function Toggle({ on, onToggle }) {
    return (
        <div className={`edt-toggle ${on ? 'on' : 'off'}`} onClick={onToggle} />
    );
}

function EdtBuilder({ user, config, onNavigate, onLogout }) {
    const [annees, setAnnees]             = useState([]);
    const [anneeSelectId, setAnneeSelectId] = useState(null);
    const [activeGroup, setActiveGroup]   = useState('B1 FI');
    const [activeFooterTab, setActiveFooterTab] = useState('formations');
    const [constraints, setConstraints]   = useState({
        anteriorites: true, salles: true, capacite: true,
        indispo: false, chevauchement: true, chargeMax: true,
    });

    const deptNom    = config?.departement?.nom || config?.departement?.titre_planning || 'Département';
    const anneeId    = anneeSelectId || config?.anneeScolaireId;
    const moduleColor = getModuleTopbarColor('edt');

    useEffect(() => {
        calendrierAPI.getAll()
            .then(res => {
                const liste = res.data?.annees || [];
                setAnnees(liste);
                if (!anneeSelectId && liste.length > 0) {
                    const active = liste.find(a => a.active);
                    if (active) setAnneeSelectId(active.id);
                }
            })
            .catch(() => {});
    }, []);

    const toggleConstraint = (key) =>
        setConstraints(c => ({ ...c, [key]: !c[key] }));

    const groups = ['B1 FI', 'B1 FA', 'B2 FI', 'B2 FA', 'B3 FI'];

    return (
        <div className="edt-layout">

            {/* ========== SIDEBAR COMMUNE ========== */}
            <AppSidebar
                activeKey="edt"
                user={user}
                config={config}
                onNavigate={onNavigate}
                onLogout={onLogout}
                annees={annees}
                anneeId={anneeId}
                onAnneeChange={setAnneeSelectId}
            />

            {/* ========== CONTENU PRINCIPAL ========== */}
            <div className="edt-main">

                {/* ZONE 1 — Bandeau pilotage */}
                <div className="edt-topbar" style={{
                    background: `linear-gradient(135deg, ${moduleColor} 0%, ${moduleColor}cc 100%)`,
                    borderBottom: 'none',
                    color: 'white'
                }}>
                    <div className="edt-topbar-title" style={{ color: 'white' }}>
                        <i className="fa-solid fa-table-cells" /> EDT Builder
                    </div>
                    <select className="edt-topbar-select" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                        <option>BUT QLIO — FI</option>
                        <option>BUT QLIO — FA</option>
                    </select>
                    <select className="edt-topbar-select" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                        {annees.map(a => <option key={a.id}>{a.libelle}</option>)}
                    </select>
                    <select className="edt-topbar-select" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                        <option>Semaine 38 — 16 sept. 2025</option>
                        <option>Semaine 39 — 23 sept. 2025</option>
                        <option>Semaine 40 — 30 sept. 2025</option>
                    </select>

                    <div className="edt-topbar-kpis">
                        <div className="edt-kpi green" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}><div className="edt-kpi-val">73%</div><div className="edt-kpi-lbl" style={{ color: 'rgba(255,255,255,0.75)' }}>Placées</div></div>
                        <div className="edt-kpi orange" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}><div className="edt-kpi-val">42</div><div className="edt-kpi-lbl" style={{ color: 'rgba(255,255,255,0.75)' }}>À placer</div></div>
                        <div className="edt-kpi blue" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}><div className="edt-kpi-val">18</div><div className="edt-kpi-lbl" style={{ color: 'rgba(255,255,255,0.75)' }}>Verrouillées</div></div>
                        <div className="edt-kpi red" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}><div className="edt-kpi-val">3</div><div className="edt-kpi-lbl" style={{ color: 'rgba(255,255,255,0.75)' }}>Conflits</div></div>
                    </div>
                    <button className="edt-btn-export" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}>
                        <i className="fa-solid fa-file-arrow-down" /> Exporter
                    </button>
                    <button className="edt-btn-export-sm" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}>
                        <i className="fa-solid fa-chevron-down" />
                    </button>
                </div>

                {/* CORPS — Zones 2+3+4 */}
                <div className="edt-body">

                    {/* ZONE 2 — Contraintes */}
                    <div className="edt-constraints">
                        <div className="edt-panel-header purple">
                            <i className="fa-solid fa-sliders" /> Contraintes
                        </div>

                        <div className="edt-constraints-section">
                            <div className="edt-constraints-title">Contraintes actives</div>

                            {[
                                { key: 'anteriorites', label: 'Antériorités',          dot: 'ok',    count: '12' },
                                { key: 'salles',       label: 'Disponibilités salles',  dot: 'ok',    count: '8'  },
                                { key: 'capacite',     label: 'Capacité max salle',     dot: 'warn',  count: '⚠2' },
                                { key: 'indispo',      label: 'Indisponib. enseignants', dot: 'off',  count: ''   },
                                { key: 'chevauchement',label: 'Chevauchement groupes',  dot: 'error', count: '✗3' },
                                { key: 'chargeMax',    label: 'Charge hebdo max',        dot: 'ok',   count: ''   },
                            ].map(c => (
                                <div key={c.key} className="edt-constraint-row">
                                    <Toggle on={constraints[c.key]} onToggle={() => toggleConstraint(c.key)} />
                                    <div className={`edt-cdot ${constraints[c.key] ? c.dot : 'off'}`} />
                                    <span className="edt-c-label" style={!constraints[c.key] ? { color: '#94a3b8' } : {}}>{c.label}</span>
                                    {c.count && <span className="edt-c-count">{c.count}</span>}
                                </div>
                            ))}
                        </div>

                        {/* Graphe PERT */}
                        <div className="edt-pert-zone">
                            <div className="edt-constraints-title">Graphe antériorités</div>
                            <div className="edt-pert-canvas">
                                <div className="edt-pert-title"><i className="fa-solid fa-diagram-project" /> Diagramme PERT</div>
                                <div className="edt-pert-row">
                                    <div className="edt-pert-node">R1.01<br/>Math</div>
                                    <div className="edt-pert-arrow"><i className="fa-solid fa-arrow-right" /></div>
                                    <div className="edt-pert-node">R1.05<br/>Stat</div>
                                    <div className="edt-pert-arrow"><i className="fa-solid fa-arrow-right" /></div>
                                    <div className="edt-pert-node">SAé 1</div>
                                </div>
                                <div className="edt-pert-row">
                                    <div className="edt-pert-node">R1.02<br/>Info</div>
                                    <div className="edt-pert-arrow"><i className="fa-solid fa-arrow-right" /></div>
                                    <div className="edt-pert-node warn" title="Contrainte violée">R1.09<br/>⚠</div>
                                </div>
                                <div className="edt-pert-hint"><i className="fa-solid fa-hand-pointer" /> Clic → détail</div>
                            </div>
                        </div>
                    </div>

                    {/* ZONE 3 — Grille EDT */}
                    <div className="edt-grid-zone">
                        <div className="edt-grid-controls">
                            <button className="edt-nav-btn"><i className="fa-solid fa-chevron-left" /></button>
                            <span className="edt-week-label">S38 — 16 sept. 2025</span>
                            <button className="edt-nav-btn"><i className="fa-solid fa-chevron-right" /></button>
                            <button className="edt-nav-btn"><i className="fa-solid fa-circle-dot" /></button>

                            <div className="edt-group-tabs">
                                {groups.map(g => (
                                    <div key={g} className={`edt-group-tab ${activeGroup === g ? 'active' : ''}`}
                                        onClick={() => setActiveGroup(g)}>{g}</div>
                                ))}
                            </div>

                            <div className="edt-legend">
                                <div className="edt-legend-item"><div className="edt-legend-sw" style={{background:'#dbeafe',border:'1px solid #93c5fd'}} />CM</div>
                                <div className="edt-legend-item"><div className="edt-legend-sw" style={{background:'#dcfce7',border:'1px solid #86efac'}} />TD</div>
                                <div className="edt-legend-item"><div className="edt-legend-sw" style={{background:'#fef9c3',border:'1px solid #fde047'}} />TP</div>
                                <div className="edt-legend-item"><div className="edt-legend-sw" style={{background:'#ede9fe',border:'1px solid #c4b5fd'}} />SAé</div>
                                <div className="edt-legend-item"><div className="edt-legend-sw" style={{background:'#fee2e2',border:'1px solid #fca5a5'}} />🔒</div>
                            </div>
                        </div>

                        <div className="edt-grid-scroll">
                            <table className="edt-table">
                                <thead>
                                    <tr>
                                        <th style={{width:58}}></th>
                                        <th>Lundi<br/><small style={{fontWeight:400,fontSize:8}}>16 sept.</small></th>
                                        <th>Mardi<br/><small style={{fontWeight:400,fontSize:8}}>17 sept.</small></th>
                                        <th>Mercredi<br/><small style={{fontWeight:400,fontSize:8}}>18 sept.</small></th>
                                        <th>Jeudi<br/><small style={{fontWeight:400,fontSize:8}}>19 sept.</small></th>
                                        <th>Vendredi<br/><small style={{fontWeight:400,fontSize:8}}>20 sept.</small></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="edt-time-col">8h–10h</td>
                                        <td className="edt-cell cm"><div className="edt-cell-label">R1.01</div><div className="edt-cell-sub">CM · Amphi A</div><div className="edt-cell-sub">Martin L.</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell tp"><div className="edt-cell-label">R1.02</div><div className="edt-cell-sub">TP · Info</div><div className="edt-cell-sub">Dupont M.</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell td"><div className="edt-cell-label">R1.05</div><div className="edt-cell-sub">TD Grp 1</div><div className="edt-cell-sub">Bernard C.</div></td>
                                    </tr>
                                    <tr>
                                        <td className="edt-time-col">10h–12h</td>
                                        <td className="edt-cell td"><div className="edt-cell-label">R1.05</div><div className="edt-cell-sub">TD Grp 2</div><div className="edt-cell-sub">Bernard C.</div></td>
                                        <td className="edt-cell sae"><div className="edt-cell-label">SAé 1</div><div className="edt-cell-sub">Groupe A</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell locked"><div className="edt-cell-label">R1.07</div><div className="edt-cell-sub">CM · Amphi</div><div className="edt-cell-sub">🔒 Verrouillé</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                    </tr>
                                    <tr>
                                        <td className="edt-time-col">14h–16h</td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell tp"><div className="edt-cell-label">R1.03</div><div className="edt-cell-sub">TP · Info</div><div className="edt-cell-sub">Leroy P.</div></td>
                                        <td className="edt-cell td"><div className="edt-cell-label">R1.09</div><div className="edt-cell-sub">TD Grp 1</div><div className="edt-cell-sub">Martin L.</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell cm"><div className="edt-cell-label">R1.11</div><div className="edt-cell-sub">CM · Amphi A</div><div className="edt-cell-sub">Dupont M.</div></td>
                                    </tr>
                                    <tr>
                                        <td className="edt-time-col">16h–18h</td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                        <td className="edt-cell sae"><div className="edt-cell-label">SAé 2</div><div className="edt-cell-sub">Groupe B</div></td>
                                        <td className="edt-cell td"><div className="edt-cell-label">R1.01</div><div className="edt-cell-sub">TD Grp 3</div><div className="edt-cell-sub">Martin L.</div></td>
                                        <td className="edt-cell empty"><i className="fa-solid fa-plus" /></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ZONE 4 — Séances à placer */}
                    <div className="edt-unplaced">
                        <div className="edt-panel-header teal">
                            <i className="fa-solid fa-layer-group" /> À placer
                            <span style={{background:'#0e7490',color:'white',borderRadius:9,padding:'1px 7px',fontSize:9,marginLeft:4}}>42</span>
                        </div>
                        <div className="edt-unplaced-list">
                            {[
                                { title:'R1.04 — TD Grp 1', type:'TD · 1h30', detail:'Salle info requise' },
                                { title:'R1.08 — CM',       type:'CM · 2h00', detail:'Amphi ≥ 80 places' },
                                { title:'SAé 3 — Grp B',    type:'SAé · 3h00', warn:'Après R1.02 séance 2' },
                                { title:'R1.11 — TP Grp 2', type:'TP · 1h30', detail:'Salle projet' },
                                { title:'R1.06 — TD Grp 1', type:'TD · 1h30', detail:'Bernard C.' },
                                { title:'R1.12 — CM',       type:'CM · 2h00', detail:'Amphi' },
                            ].map((s, i) => (
                                <div key={i} className="edt-seance-card" draggable>
                                    <div className="edt-sc-title">{s.title}</div>
                                    <div className="edt-sc-type">{s.type}</div>
                                    {s.detail && <div className="edt-sc-detail"><i className="fa-solid fa-door-open" />{s.detail}</div>}
                                    {s.warn   && <div className="edt-sc-warn"><i className="fa-solid fa-triangle-exclamation" />{s.warn}</div>}
                                </div>
                            ))}
                        </div>
                        <div className="edt-unplaced-hint">
                            <i className="fa-solid fa-hand-pointer" /> Glisser dans la grille
                        </div>
                    </div>

                </div>{/* fin edt-body */}

                {/* ZONE 5 — Indicateurs charge */}
                <div className="edt-footer">
                    <div className="edt-footer-tabs">
                        {[
                            { key:'formations', label:'Charge formations',    icon:'fa-chart-bar' },
                            { key:'enseignants',label:'Charge enseignants',   icon:'fa-person-chalkboard' },
                            { key:'alertes',    label:'Alertes (3)',          icon:'fa-triangle-exclamation', color:'#f97316' },
                        ].map(t => (
                            <div key={t.key}
                                className={`edt-footer-tab ${activeFooterTab === t.key ? 'active' : ''}`}
                                onClick={() => setActiveFooterTab(t.key)}
                            >
                                <i className={`fa-solid ${t.icon}`} style={t.color ? {color:t.color} : {}} />
                                {t.label}
                            </div>
                        ))}
                    </div>

                    <div className="edt-footer-body">
                        {/* Charge formations */}
                        <div>
                            <div className="edt-charge-title">Taux de charge par formation — Semaine 38</div>
                            {[
                                { label:'B1 FI', pct:72,  cls:'ok'   },
                                { label:'B1 FA', pct:91,  cls:'warn' },
                                { label:'B2 FI', pct:65,  cls:'ok'   },
                                { label:'B2 FA', pct:108, cls:'over' },
                                { label:'B3 FI', pct:58,  cls:'ok'   },
                            ].map(b => (
                                <div key={b.label} className="edt-bar-row">
                                    <div className="edt-bar-label">{b.label}</div>
                                    <div className="edt-bar-track">
                                        <div className={`edt-bar-fill ${b.cls}`} style={{width:`${Math.min(b.pct,100)}%`}} />
                                    </div>
                                    <div className={`edt-bar-pct ${b.cls}`}>{b.pct}%</div>
                                </div>
                            ))}
                        </div>

                        {/* Charge enseignants */}
                        <div>
                            <div className="edt-charge-title">Charge enseignants — S38/S39/S40</div>
                            <table className="edt-charge-table">
                                <thead>
                                    <tr><th>Enseignant</th><th>S38</th><th>S39</th><th>S40</th><th>Cumulé</th></tr>
                                </thead>
                                <tbody>
                                    {[
                                        { nom:'Martin L.',  s38:'8h',  s39:'6h',  s40:'14h', cum:'28h',  c38:'ok',  c39:'ok',  c40:'warn', cc:'ok'  },
                                        { nom:'Dupont M.',  s38:'12h', s39:'8h',  s40:'7h',  cum:'27h',  c38:'warn',c39:'ok',  c40:'ok',   cc:'warn'},
                                        { nom:'Bernard C.', s38:'18h', s39:'4h',  s40:'2h',  cum:'24h',  c38:'over',c39:'ok',  c40:'low',  cc:'warn'},
                                        { nom:'Leroy P.',   s38:'6h',  s39:'6h',  s40:'6h',  cum:'18h',  c38:'ok',  c39:'ok',  c40:'ok',   cc:'ok'  },
                                    ].map(e => (
                                        <tr key={e.nom}>
                                            <td>{e.nom}</td>
                                            <td><span className={`t-${e.c38}`}>{e.s38}</span></td>
                                            <td><span className={`t-${e.c39}`}>{e.s39}</span></td>
                                            <td><span className={`t-${e.c40}`}>{e.s40}</span></td>
                                            <td><span className={`t-${e.cc}`}>{e.cum}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

            </div>{/* fin edt-main */}
        </div>
    );
}

export default EdtBuilder;
